import { useState, useEffect, useRef, useCallback } from "react";

const C = {
  primary: "#1a3a5c",
  border: "#d4c99a",
  success: "#2d6a4f",
  danger: "#c1121f",
  warning: "#e76f51",
  muted: "#6b7280",
  bg: "#fdfcf8",
  surface: "#f5f1e8",
};

// Evaluations are stored in our own DB (employee_evaluations), not Google Sheets.
// REACT_APP_API_BASE_URL already ends in /api — never repeat it in the path.
const API = process.env.REACT_APP_API_BASE_URL;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const urdu = {
  fontFamily: "'Noto Nastaliq Urdu', serif",
  direction: "rtl",
  fontSize: "11px",
  opacity: 0.8,
  display: "inline",
};

function SectionHeader({ en, ur, danger }) {
  return (
    <div style={{
      background: danger ? C.danger : C.primary,
      color: "#fff", padding: "10px 14px", fontSize: "13px",
      fontWeight: "bold", letterSpacing: "0.04em",
      display: "flex", justifyContent: "space-between",
      alignItems: "center", flexWrap: "wrap", gap: "4px",
    }}>
      <span>{en}</span>
      <span style={{ ...urdu, fontSize: "12px", opacity: 1 }}>{ur}</span>
    </div>
  );
}

function RadioGroup({ name, options, value, onChange, vertical }) {
  return (
    <div style={{ display: "flex", flexDirection: vertical ? "column" : "row", gap: vertical ? "8px" : "14px", flexWrap: "wrap" }}>
      {options.map((opt) => (
        <label key={opt.val} style={{
          display: "flex", alignItems: "center", gap: "6px",
          cursor: "pointer", fontSize: "13px",
          fontWeight: value === opt.val ? "bold" : "normal",
          color: opt.color || "inherit", padding: "4px 0",
        }}>
          <input type="radio" name={name} value={opt.val}
            checked={value === opt.val} onChange={() => onChange(opt.val)}
            style={{ accentColor: C.primary, width: "16px", height: "16px" }}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

function TextInput({ value, onChange }) {
  return (
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%", boxSizing: "border-box",
        border: "none", borderBottom: `1.5px solid ${C.border}`,
        background: "transparent", padding: "6px 2px",
        fontSize: "14px", outline: "none", fontFamily: "Georgia, serif",
      }}
    />
  );
}

function ScoreChip({ val, max, danger }) {
  return (
    <span style={{ fontWeight: "bold", color: danger ? C.danger : val === max ? C.success : C.primary }}>
      {val}<span style={{ fontWeight: "normal", color: C.muted, fontSize: "11px" }}>/{max}</span>
    </span>
  );
}

function TableHead({ isMobile }) {
  if (isMobile) return null;
  return (
    <div style={{ display: "flex", background: C.surface, borderBottom: `1px solid ${C.border}`, fontSize: "12px", fontWeight: "bold" }}>
      <div style={{ flex: "1 1 55%", padding: "8px 14px" }}>Criteria │ <span style={urdu}>معیار</span></div>
      <div style={{ flex: "0 0 150px", padding: "8px 14px", textAlign: "center" }}>Status │ <span style={urdu}>حیثیت</span></div>
      <div style={{ flex: "0 0 80px", padding: "8px 14px", textAlign: "center", borderLeft: `1px solid ${C.border}` }}>Max Pts</div>
    </div>
  );
}

function CriteriaRow({ label, urduText, status, score, isMobile, isLast }) {
  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", borderBottom: isLast ? "none" : `1px solid ${C.border}`, alignItems: isMobile ? "stretch" : "center" }}>
      <div style={{ flex: "1 1 55%", padding: "10px 14px", fontSize: "13px", lineHeight: "1.6", color: "#222", borderBottom: isMobile ? `1px solid ${C.border}` : "none" }}>
        {label}
        {urduText && <><br /><span style={urdu}>{urduText}</span></>}
      </div>
      <div style={{ flex: "0 0 auto", padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: isMobile ? "flex-start" : "center", minWidth: isMobile ? "auto" : "150px", background: isMobile ? C.surface : "transparent" }}>
        {status}
      </div>
      {score !== undefined && (
        <div style={{ flex: "0 0 80px", padding: "10px 14px", textAlign: "center", fontWeight: "bold", fontSize: "13px", color: C.primary, background: C.surface, borderLeft: isMobile ? "none" : `1px solid ${C.border}`, borderTop: isMobile ? `1px solid ${C.border}` : "none" }}>
          {score}
        </div>
      )}
    </div>
  );
}

function Section({ children, style }) {
  return (
    <div style={{ marginBottom: "1.25rem", border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden", fontFamily: "Georgia, serif", ...style }}>
      {children}
    </div>
  );
}

function SignaturePad({ label, urText, onSave, savedImage }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [saved, setSaved] = useState(false);
  const lastPos = useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const pos = getPos(e, canvas);
    setIsDrawing(true);
    setSaved(false);
    lastPos.current = pos;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1, 0, Math.PI * 2);
    ctx.fillStyle = "#1a3a5c";
    ctx.fill();
  }, []);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1a3a5c";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    lastPos.current = pos;
    setHasDrawn(true);
  }, [isDrawing]);

  const endDraw = useCallback(() => {
    setIsDrawing(false);
    lastPos.current = null;
  }, []);

  const clearPad = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    setSaved(false);
    onSave(null);
  };

  const saveSig = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    onSave(dataUrl);
    setSaved(true);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    // Restore saved sig into canvas
    if (savedImage) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = savedImage;
      setHasDrawn(true);
      setSaved(true);
    }
  }, []);

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ background: C.surface, padding: "10px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: "12px", fontWeight: "bold", color: C.muted }}>
          {label} &nbsp;<span style={urdu}>{urText}</span>
        </div>
        {saved && (
          <span style={{ fontSize: "11px", color: C.success, fontWeight: "bold" }}>✔ Saved</span>
        )}
      </div>

      {/* Canvas */}
      <div style={{ background: "#fff", position: "relative" }}>
        <canvas
          ref={canvasRef}
          width={600}
          height={130}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
          style={{
            width: "100%",
            height: "130px",
            display: "block",
            cursor: "crosshair",
            touchAction: "none",
          }}
        />
        {!hasDrawn && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            pointerEvents: "none", color: "#ccc", fontSize: "13px", fontStyle: "italic",
          }}>
            ✍ Sign here
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ background: C.surface, padding: "8px 14px", borderTop: `1px solid ${C.border}`, display: "flex", gap: "8px", justifyContent: "flex-end" }}>
        <button
          onClick={clearPad}
          style={{
            padding: "6px 16px", fontSize: "12px", borderRadius: "4px",
            border: `1px solid ${C.border}`, background: "#fff",
            cursor: "pointer", color: C.muted, fontFamily: "Georgia, serif",
          }}
        >
          🗑 Clear
        </button>
        <button
          onClick={saveSig}
          disabled={!hasDrawn}
          style={{
            padding: "6px 16px", fontSize: "12px", borderRadius: "4px",
            border: "none",
            background: hasDrawn ? C.primary : "#ccc",
            color: "#fff", cursor: hasDrawn ? "pointer" : "not-allowed",
            fontWeight: "bold", fontFamily: "Georgia, serif",
          }}
        >
          ✔ Save Signature
        </button>
      </div>
    </div>
  );
}

export default function EmployeeEvaluation() {
  const isMobile = useIsMobile();
  const [basic, setBasic] = useState({ name: "", id: "", role: "", site: "", supervisor: "" });
  const [attendance, setAttendance] = useState(null);
  const [panelPct, setPanelPct] = useState("");
  const [workQuality, setWorkQuality] = useState(null);
  const [taskReliability, setTaskReliability] = useState(null);
  const [commClarity, setCommClarity] = useState(null);
  const [problemReporting, setProblemReporting] = useState(null);
  const [handlesIssues, setHandlesIssues] = useState(null);
  const [takesInitiative, setTakesInitiative] = useState(null);
  const [ppePcompliance, setPpePcompliance] = useState(null);
  const [safetyHandling, setSafetyHandling] = useState(null);
  const [safetyTraining, setSafetyTraining] = useState(null);
  const [ehsViolation, setEhsViolation] = useState("none");
  const [remarks, setRemarks] = useState("");
  const [supervisorScore, setSupervisorScore] = useState(0);
  const [supervisorSig, setSupervisorSig] = useState(null);
  const [employeeSig, setEmployeeSig] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle"); // "idle" | "saving" | "success" | "error"
  const [saveMessage, setSaveMessage] = useState("");

  const handleSave = async () => {
    if (!basic.name.trim()) {
      setSaveStatus("error");
      setSaveMessage("Employee Name is required before saving.");
      return;
    }
    if (!API) {
      setSaveStatus("error");
      setSaveMessage("REACT_APP_API_BASE_URL is not set — cannot reach the server.");
      return;
    }

    setSaveStatus("saving");
    setSaveMessage("");

    const payload = {
      timestamp: new Date().toISOString(),
      employeeName: basic.name,
      employeeId: basic.id,
      role: basic.role,
      site: basic.site,
      supervisor: basic.supervisor,
      // Section scores
      attendance: attendance,
      attendanceScore: s2,
      panelProductivityPct: panelPct,
      workQuality: workQuality,
      taskReliability: taskReliability,
      productivityScore: s3,
      commClarity: commClarity,
      problemReporting: problemReporting,
      reportingScore: s4,
      handlesIssues: handlesIssues,
      takesInitiative: takesInitiative,
      problemSolvingScore: s5,
      ehsViolation: ehsViolation,
      ppePcompliance: ppePcompliance,
      safetyHandling: safetyHandling,
      safetyTraining: safetyTraining,
      ehsScore: s6,
      supervisorRemarks: remarks,
      supervisorScore: s7,
      totalScore: total,
      outcome: outcome.label,
      // The actual PNG data URLs, not booleans — the previous version sent
      // `!!sig`, so no signature was ever persisted anywhere.
      signature: supervisorSig,
      employeeSignature: employeeSig,
    };

    try {
      const res = await fetch(`${API}/evaluations/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // fetch only rejects on network failure, so an HTTP error still lands
      // here as a resolved promise — check the status explicitly rather than
      // assuming success the way the old no-cors call did.
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || `Server returned HTTP ${res.status}`);
      }

      setSaveStatus("success");
      setSaveMessage(`✔ Saved — record #${data.evaluation_id} at ${new Date().toLocaleTimeString()}`);
    } catch (err) {
      setSaveStatus("error");
      setSaveMessage(`❌ Save failed: ${err.message}. Nothing was recorded — please retry.`);
    }
  };

  const s2 = attendance === "pass" ? 25 : 0;
  const s3 = (() => {
    let pts = 0;
    const pct = parseFloat(panelPct);
    if (!isNaN(pct)) { if (pct >= 100) pts += 12; else if (pct >= 80) pts += 10; else if (pct >= 60) pts += 7; else pts += 3; }
    if (workQuality === "good") pts += 7;
    if (taskReliability === "yes") pts += 6;
    return Math.min(25, pts);
  })();
  const s4 = (commClarity === "yes" ? 13 : 0) + (problemReporting === "yes" ? 12 : 0);
  const s5 = (handlesIssues === "yes" ? 5 : 0) + (takesInitiative === "yes" ? 5 : 0);
  const s6 = (() => {
    if (ehsViolation === "major" || ehsViolation === "complaints2") return 0;
    return (ppePcompliance === "yes" ? 4 : 0) + (safetyHandling === "yes" ? 3 : 0) + (safetyTraining === "yes" ? 3 : 0);
  })();
  const s7 = Math.min(5, Math.max(0, supervisorScore));
  const total = s2 + s3 + s4 + s5 + s6 + s7;
  const ehsOverride = ehsViolation === "major" || ehsViolation === "complaints2";

  const outcome = (() => {
    if (ehsViolation === "major") return { label: "Immediate Action", urduText: "فوری کارروائی", stars: 0, color: C.danger };
    if (ehsViolation === "complaints2") return { label: "Promotion Blocked", urduText: "ترقی روک دی گئی", stars: 0, color: C.warning };
    if (total >= 90) return { label: "Excellent", urduText: "بہترین", stars: 3, color: C.success };
    if (total >= 80) return { label: "Good", urduText: "اچھا", stars: 2, color: "#1d6fa5" };
    return { label: "Average", urduText: "اوسط", stars: 1, color: C.warning };
  })();

  const yesNo = [{ val: "yes", label: "YES ✔" }, { val: "no", label: "NO ✖" }];
  const basicFields = [
    { label: "Employee Name", ur: "ملازم کا نام", field: "name" },
    { label: "Employee ID", ur: "ملازم نمبر", field: "id" },
    { label: "Role  (FO / Technician / Supervisor)", ur: "عہدہ", field: "role" },
    { label: "Site", ur: "سائٹ / جگہ", field: "site" },

    { label: "Supervisor", ur: "نگران کا نام", field: "supervisor" },
  ];

  return (
    <div style={{ height: "100vh", overflowY: "auto", overflowX: "hidden", boxSizing: "border-box", WebkitOverflowScrolling: "touch" }}>
      <div style={{ maxWidth: "820px", margin: "0 auto", padding: isMobile ? "10px 8px" : "24px 16px", fontFamily: "Georgia, serif", background: C.bg, color: "#1a1a1a", minHeight: "100%" }}>

        {/* Title */}
        <div style={{ background: C.primary, color: "#fff", textAlign: "center", padding: isMobile ? "14px 10px" : "18px 24px", borderRadius: "8px 8px 0 0", border: `2px solid ${C.primary}` }}>
          <div style={{ fontSize: isMobile ? "14px" : "18px", fontWeight: "bold", letterSpacing: "0.05em", marginBottom: "4px" }}>EMPLOYEE PERFORMANCE EVALUATION</div>
          <div style={{ ...urdu, fontSize: "13px", color: "#d4c99a", opacity: 1, display: "block" }}>ملازم کی کارکردگی کا جائزہ</div>
          <div style={{ fontSize: "11px", color: "#a0b8d0", marginTop: "6px", fontStyle: "italic" }}>Weekly Assessment Form &nbsp;|&nbsp; <span style={urdu}>ہفتہ وار جائزہ فارم</span></div>
        </div>

        {/* Section 1 */}
        <Section>
          <SectionHeader en="SECTION 1 · BASIC INFORMATION" ur="بنیادی معلومات" />
          {basicFields.map((f, i) => (
            <div key={f.field} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", borderBottom: i < basicFields.length - 1 ? `1px solid ${C.border}` : "none", padding: "10px 14px", gap: "6px", alignItems: isMobile ? "flex-start" : "center" }}>
              <div style={{ flex: "0 0 auto", minWidth: "190px", fontSize: "13px", fontWeight: "500", color: "#333" }}>
                {f.label} &nbsp;<span style={urdu}>{f.ur}</span>
              </div>
              <div style={{ flex: 1, width: "100%" }}>
                <TextInput value={basic[f.field]} onChange={(v) => setBasic({ ...basic, [f.field]: v })} />
              </div>
            </div>
          ))}
          <div style={{ background: "#fff8e1", padding: "10px 14px", fontSize: "12px", color: C.warning, fontStyle: "italic", textAlign: "center", borderTop: `1px solid ${C.border}` }}>
            ⚠ ALL sections must be completed before final scoring &nbsp;│&nbsp; <span style={urdu}>اسکور سے پہلے تمام حصے مکمل کریں</span>
          </div>
        </Section>

        {/* Section 2 */}
        <Section>
          <SectionHeader en="SECTION 2 – ATTENDANCE" ur="حاضری" />
          <TableHead isMobile={isMobile} />
          <CriteriaRow isMobile={isMobile} isLast
            label="≥ 90% of total working days (weekly)"
            urduText="کل کام کے دنوں میں کم از کم 90٪ حاضری"
            status={<RadioGroup name="attendance" options={[{ val: "pass", label: "✔ Present" }, { val: "fail", label: "✖ Absent" }]} value={attendance} onChange={setAttendance} vertical={isMobile} />}
            score={<ScoreChip val={s2} max={25} />}
          />
        </Section>

        {/* Section 3 */}
        <Section>
          <SectionHeader en="SECTION 3 – PRODUCTIVITY" ur="کارکردگی" />
          <TableHead isMobile={isMobile} />
          <CriteriaRow isMobile={isMobile}
            label="Panel Cleaning Productivity = (Panels Cleaned ÷ Team Members)"
            urduText="پینل صاف کرنے کی کارکردگی = (صاف کیے گئے پینلز ÷ ٹیم کے افراد)"
            status={
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <input type="number" min="0" max="200" value={panelPct} onChange={(e) => setPanelPct(e.target.value)} placeholder="0"
                  style={{ width: "70px", border: `1.5px solid ${C.border}`, borderRadius: "4px", padding: "6px 8px", fontSize: "14px", textAlign: "center" }} />
                <span style={{ fontWeight: "bold" }}>%</span>
              </div>
            }
            score={<ScoreChip val={s3} max={25} />}
          />
          <CriteriaRow isMobile={isMobile}
            label="Work Quality"
            urduText="کام کا معیار = اچھا یا خراب"
            status={<RadioGroup name="workQuality" options={[{ val: "good", label: "GOOD ✔" }, { val: "bad", label: "BAD ✖" }]} value={workQuality} onChange={setWorkQuality} vertical={isMobile} />}
          />
          <CriteriaRow isMobile={isMobile} isLast
            label="Task Reliability – Employee owns & takes responsibility of assigned task"
            urduText="ذمہ داری لینا = ہاں یا نہیں"
            status={<RadioGroup name="taskReliability" options={yesNo} value={taskReliability} onChange={setTaskReliability} vertical={isMobile} />}
          />
        </Section>

        {/* Section 4 */}
        <Section>
          <SectionHeader en="SECTION 4 – REPORTING & COMMUNICATION" ur="رپورٹنگ اور بات چیت" />
          <TableHead isMobile={isMobile} />
          <CriteriaRow isMobile={isMobile}
            label="Communication Clarity – Understands & conveys messages clearly"
            urduText="بات سمجھنا اور واضح بتانا = ہاں یا نہیں"
            status={<RadioGroup name="commClarity" options={yesNo} value={commClarity} onChange={setCommClarity} vertical={isMobile} />}
            score={<ScoreChip val={s4} max={25} />}
          />
          <CriteriaRow isMobile={isMobile} isLast
            label="Problem Reporting – Reports equipment/panel/client property damage"
            urduText="مسئلہ رپورٹ کرنا = ہاں یا نہیں"
            status={<RadioGroup name="problemReporting" options={yesNo} value={problemReporting} onChange={setProblemReporting} vertical={isMobile} />}
          />
        </Section>

        {/* Section 5 */}
        <Section>
          <SectionHeader en="SECTION 5 – PROBLEM SOLVING & INITIATIVE" ur="مسئلہ حل کرنا اور پہل کرنا" />
          <TableHead isMobile={isMobile} />
          <CriteriaRow isMobile={isMobile}
            label="Handles Issues Independently"
            urduText="خود مسئلہ حل کرتا ہے"
            status={<RadioGroup name="handlesIssues" options={yesNo} value={handlesIssues} onChange={setHandlesIssues} vertical={isMobile} />}
            score={<ScoreChip val={s5} max={10} />}
          />
          <CriteriaRow isMobile={isMobile} isLast
            label="Takes Initiatives"
            urduText="خود سے پہل کرتا ہے"
            status={<RadioGroup name="takesInitiative" options={yesNo} value={takesInitiative} onChange={setTakesInitiative} vertical={isMobile} />}
          />
        </Section>

        {/* Section 6: EHS */}
        <Section style={{ border: `1.5px solid ${C.danger}` }}>
          <SectionHeader en="SECTION 6 – EHS COMPLIANCE" ur="حفاظتی اصولوں پر عمل" danger />
          <div style={{ background: "#fff3f3", padding: "10px 14px", borderBottom: `1px solid ${C.border}`, fontSize: "12px", fontWeight: "bold" }}>
            ⚠ THIS RULE OVERRIDES ALL SCORING &nbsp;│&nbsp; <span style={urdu}>یہ اصول سب سے اہم ہے</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", borderBottom: `1px solid ${C.border}` }}>
            {[
              { text: "1 minor complaint in 6 months", ur: "چھ مہینے میں 1 چھوٹی شکایت = قابل قبول", badge: "✅ ALLOWED", color: C.success },
              { text: "2 complaints in 6 months", ur: "چھ مہینے میں 2 شکایات = ترقی روک دی جائے گی", badge: "❌ BLOCKED", color: C.warning },
              { text: "Major violation (life-threatening / financial / client)", ur: "بڑی خلاف ورزی = فوری کارروائی", badge: "❌ IMMEDIATE", color: C.danger },
            ].map((r, i) => (
              <div key={i} style={{ padding: "10px 14px", fontSize: "12px", lineHeight: "1.5", borderRight: !isMobile && i < 2 ? `1px solid ${C.border}` : "none", borderBottom: isMobile && i < 2 ? `1px solid ${C.border}` : "none", background: i === 2 ? "#fff3f3" : "transparent" }}>
                <div style={{ marginBottom: "4px" }}>{r.text}</div>
                <div style={urdu}>{r.ur}</div>
                <div style={{ marginTop: "6px", fontWeight: "bold", color: r.color }}>{r.badge}</div>
              </div>
            ))}
          </div>

          <div style={{ padding: "10px 14px", background: "#f5f0e8", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: "12px", fontWeight: "bold", marginBottom: "8px" }}>EHS Status Override &nbsp;│&nbsp; <span style={urdu}>خلاف ورزی کی قسم منتخب کریں</span></div>
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "10px" : "16px", flexWrap: "wrap" }}>
              {[
                { val: "none", label: "No violation", color: C.success },
                { val: "minor1", label: "1 Minor (allowed)", color: "#555" },
                { val: "complaints2", label: "2 Complaints (blocks promo)", color: C.warning },
                { val: "major", label: "Major (immediate action)", color: C.danger },
              ].map((opt) => (
                <label key={opt.val} style={{ display: "flex", alignItems: "center", gap: "7px", cursor: "pointer", fontSize: "13px", fontWeight: ehsViolation === opt.val ? "bold" : "normal", color: opt.color, padding: isMobile ? "4px 0" : "0" }}>
                  <input type="radio" name="ehsViolation" value={opt.val} checked={ehsViolation === opt.val} onChange={() => setEhsViolation(opt.val)} style={{ accentColor: C.primary, width: "16px", height: "16px" }} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ padding: "8px 14px", fontWeight: "bold", fontSize: "12px", background: C.surface, borderBottom: `1px solid ${C.border}` }}>
            Compliance Checklist &nbsp;│&nbsp; <span style={urdu}>تعمیل کی فہرست</span>
          </div>
          <TableHead isMobile={isMobile} />
          <CriteriaRow isMobile={isMobile} label="PPE Compliance" urduText="سیفٹی سامان کا استعمال"
            status={<RadioGroup name="ppeCom" options={yesNo} value={ppePcompliance} onChange={setPpePcompliance} vertical={isMobile} />}
            score={<ScoreChip val={s6} max={10} danger={ehsOverride} />}
          />
          <CriteriaRow isMobile={isMobile} label="Safety Equipment Handling" urduText="سیفٹی سامان کو صحیح طریقے سے استعمال کرنا"
            status={<RadioGroup name="safetyHandling" options={yesNo} value={safetyHandling} onChange={setSafetyHandling} vertical={isMobile} />}
          />
          <CriteriaRow isMobile={isMobile} isLast label="Safety Training Compliance" urduText="سیفٹی ٹریننگ پر عمل کرنا"
            status={<RadioGroup name="safetyTraining" options={yesNo} value={safetyTraining} onChange={setSafetyTraining} vertical={isMobile} />}
          />
        </Section>

        {/* Section 7 */}
        <Section>
          <SectionHeader en="SECTION 7 – SUPERVISOR'S FEEDBACK" ur="نگران کی رائے" />
          <div style={{ padding: "14px" }}>
            <div style={{ fontSize: "13px", fontWeight: "500", marginBottom: "8px" }}>Remarks (Overall Performance) │ <span style={urdu}>مجموعی کارکردگی کے بارے میں رائے</span></div>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={isMobile ? 4 : 3} placeholder="Enter supervisor remarks here..."
              style={{ width: "100%", boxSizing: "border-box", border: `1.5px solid ${C.border}`, borderRadius: "6px", padding: "10px", fontSize: "13px", fontFamily: "Georgia, serif", resize: "vertical" }}
            />
            <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "13px", fontWeight: "500" }}>Supervisor Score (0–5) │ <span style={urdu}>زیادہ سے زیادہ نمبر: 5</span></span>
              <input type="number" min="0" max="5" value={supervisorScore} onChange={(e) => setSupervisorScore(Number(e.target.value))}
                style={{ width: "64px", border: `1.5px solid ${C.border}`, borderRadius: "4px", padding: "6px 8px", fontSize: "14px", textAlign: "center" }} />
              <span style={{ color: C.muted, fontSize: "12px" }}>Max: 5 pts</span>
            </div>
          </div>
        </Section>

        {/* Final Score */}
        <Section style={{ border: `2px solid ${C.primary}` }}>
          <SectionHeader en="FINAL SCORE & DECISION" ur="آخری اسکور اور فیصلہ" />

          {!isMobile && (
            <div style={{ display: "flex", background: C.surface, borderBottom: `1px solid ${C.border}`, fontSize: "12px", fontWeight: "bold" }}>
              <div style={{ flex: 1, padding: "8px 14px", textAlign: "center" }}>Score Range │ <span style={urdu}>درجہ</span></div>
              <div style={{ flex: 1, padding: "8px 14px", textAlign: "center", borderLeft: `1px solid ${C.border}` }}>Outcome │ <span style={urdu}>نتیجہ</span></div>
              <div style={{ flex: "0 0 80px", padding: "8px 14px", textAlign: "center", borderLeft: `1px solid ${C.border}` }}>Badge</div>
            </div>
          )}

          {[
            { range: "≥ 90", outcome: "Excellent", ur: "بہترین", stars: "⭐⭐⭐", active: total >= 90 && !ehsOverride },
            { range: "80 – 89", outcome: "Good", ur: "اچھا", stars: "⭐⭐", active: total >= 80 && total < 90 && !ehsOverride },
            { range: "< 80", outcome: "Average", ur: "اوسط", stars: "⭐", active: total < 80 && !ehsOverride },
          ].map((row) => (
            <div key={row.range} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", background: row.active ? "#e8f5e9" : "transparent", borderBottom: `1px solid ${C.border}`, fontSize: "13px" }}>
              <div style={{ flex: 1, padding: "10px 14px", textAlign: "center", fontWeight: row.active ? "bold" : "normal" }}>{row.range}</div>
              <div style={{ flex: 1, padding: "10px 14px", textAlign: "center", fontWeight: row.active ? "bold" : "normal", borderLeft: isMobile ? "none" : `1px solid ${C.border}`, borderTop: isMobile ? `1px solid ${C.border}` : "none" }}>
                {row.outcome} │ <span style={urdu}>{row.ur}</span>
              </div>
              <div style={{ flex: "0 0 auto", padding: "10px 14px", textAlign: "center", fontSize: "18px", borderLeft: isMobile ? "none" : `1px solid ${C.border}`, borderTop: isMobile ? `1px solid ${C.border}` : "none" }}>{row.stars}</div>
            </div>
          ))}

          {/* Breakdown */}
          <div style={{ padding: "12px 14px", borderTop: `1px solid ${C.border}`, background: "#faf8f2" }}>
            <div style={{ fontSize: "11px", color: C.muted, fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "10px" }}>Score Breakdown</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(6, 1fr)", gap: "8px" }}>
              {[
                { label: "Attendance", val: s2, max: 25 },
                { label: "Productivity", val: s3, max: 25 },
                { label: "Reporting", val: s4, max: 25 },
                { label: "Problem", val: s5, max: 10 },
                { label: "EHS", val: s6, max: 10 },
                { label: "Supervisor", val: s7, max: 5 },
              ].map((sec) => (
                <div key={sec.label} style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: "6px", padding: "8px 6px", textAlign: "center" }}>
                  <div style={{ fontSize: "10px", color: C.muted, marginBottom: "4px", lineHeight: 1.2 }}>{sec.label}</div>
                  <div style={{ fontSize: "15px", fontWeight: "bold", color: sec.val === sec.max ? C.success : C.primary }}>
                    {sec.val}<span style={{ fontSize: "10px", color: C.muted }}>/{sec.max}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div style={{ padding: "16px 14px", borderTop: `2px solid ${C.primary}`, display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", gap: "16px" }}>
            <div>
              <div style={{ fontSize: "12px", color: C.muted, marginBottom: "4px" }}>TOTAL SCORE │ <span style={urdu}>کل نمبر</span></div>
              <div style={{ fontSize: isMobile ? "48px" : "52px", fontWeight: "bold", color: outcome.color, lineHeight: 1 }}>
                {total}<span style={{ fontSize: "20px", color: C.muted, fontWeight: "normal" }}> / 100</span>
              </div>
            </div>
            <div style={{ textAlign: isMobile ? "left" : "center" }}>
              <div style={{ fontSize: isMobile ? "18px" : "22px", background: outcome.color, color: "#fff", padding: "10px 24px", borderRadius: "6px", fontWeight: "bold", display: "inline-block" }}>
                {outcome.label}
              </div>
              <div style={{ ...urdu, fontSize: "13px", marginTop: "6px", color: outcome.color, display: "block" }}>{outcome.urduText}</div>
              {outcome.stars > 0 && <div style={{ fontSize: "22px", marginTop: "4px" }}>{"⭐".repeat(outcome.stars)}</div>}
            </div>
          </div>
        </Section>

        {/* Signatures */}
        <div style={{ marginBottom: "12px" }}>
          <div style={{ fontSize: "12px", fontWeight: "bold", color: C.muted, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Signatures &nbsp;│&nbsp; <span style={urdu}>دستخط</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "14px" }}>
            <SignaturePad
              label="Supervisor Signature"
              urText="نگران کے دستخط"
              onSave={setSupervisorSig}
              savedImage={supervisorSig}
            />
          </div>
        </div>

        {/* ── SAVE TO GOOGLE SHEETS ── */}
        <div style={{
          position: "sticky", bottom: 0, zIndex: 100,
          background: C.primary, borderRadius: "8px",
          padding: "14px 18px", marginBottom: "14px",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.18)",
          display: "flex", flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between", gap: "12px",
        }}>

          <div style={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "stretch" : "flex-end", gap: "6px" }}>
            <button
              onClick={handleSave}
              disabled={saveStatus === "saving"}
              style={{
                padding: "12px 32px",
                fontSize: "14px", fontWeight: "bold",
                borderRadius: "6px", border: "none",
                background: saveStatus === "saving" ? "#6b8caa"
                  : saveStatus === "success" ? C.success
                  : "#f0c040",
                color: saveStatus === "success" ? "#fff" : "#1a1a1a",
                cursor: saveStatus === "saving" ? "not-allowed" : "pointer",
                fontFamily: "Georgia, serif",
                letterSpacing: "0.04em",
                width: isMobile ? "100%" : "auto",
                transition: "background 0.2s",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              }}
            >
              {saveStatus === "saving" ? "⏳ Saving..." : saveStatus === "success" ? "✔ Saved!" : "💾 SAVE EVALUATION"}
            </button>

            {saveMessage && (
              <div style={{
                fontSize: "12px", padding: "6px 10px", borderRadius: "4px",
                background: saveStatus === "error" ? "#fde8e8" : "#e8f5e9",
                color: saveStatus === "error" ? C.danger : C.success,
                fontWeight: "500", maxWidth: isMobile ? "100%" : "300px",
                textAlign: isMobile ? "center" : "right",
              }}>
                {saveMessage}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", fontSize: "11px", color: C.muted, fontStyle: "italic", paddingBottom: "24px" }}>
          Evaluation Day: Saturday (End of Each Week) │ <span style={urdu}>جائزہ کا دن: ہفتہ</span>
          <br />
          <span style={{ letterSpacing: "0.08em", color: "#aaa" }}>CONFIDENTIAL – FOR INTERNAL USE ONLY</span>
        </div>
      </div>
    </div>
  );
} 